import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, FolderKanban } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { PageHeader } from '@/components/app/PageHeader';
import { EmptyState, Panel, PanelHeader } from '@/components/app/States';
import { Note } from '@/components/app/Badges';
import { ExampleDataNote, StatusPill, useDriftRefs } from '@/components/app/DriftUI';
import { DriftFilterBar, useDriftFilters } from '@/components/app/DriftFilters';
import { byUnitText, hoursText, listProjects } from '@/lib/driftApi';
import { formatDateShort } from '@/domain/time';
import { fmtNumber } from '@/domain/numbers';
import { cn } from '@/lib/utils';

/*
  Prosjekter: binder timer, maskintimer, tillegg og vareforbruk sammen per (fiktivt) prosjekt.
  Alle tall gjelder valgt periode og kan åpnes som registreringer. Ingen økonomi – kun mengder og tid.
*/

const NO_KEYS = [];
const VIEWS = [
  { id: 'aktiv', label: 'Aktive' },
  { id: 'avsluttet', label: 'Avsluttede' },
  { id: 'alle', label: 'Alle' },
];

export function ProjectStatusPill({ status, ...props }) {
  return (
    <StatusPill tone={status === 'avsluttet' ? 'neutral' : 'green'} {...props}>
      {status === 'avsluttet' ? 'Avsluttet' : 'Aktivt'}
    </StatusPill>
  );
}

function ProjectCard({ p, refs, search }) {
  const per = p.period;
  const forbruk = byUnitText(per.consumptionByUnit, refs?.units);
  return (
    <Link to={`/prosjekter/${p.id}${search}`} className="group/card flex min-w-0 flex-col bg-white px-5 py-4 transition-colors duration-150 hover:bg-[var(--hr-surface-2)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--hr-focus)]" data-testid={`project-card-${p.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="truncate text-[16px] font-semibold text-[var(--hr-heading)]">{p.name}</div>
          <div className="mt-0.5 truncate text-[13px] text-[var(--hr-muted)]">{p.customer} · {p.location}</div>
        </div>
        <ProjectStatusPill status={p.status} className="shrink-0" />
      </div>
      <dl className="mt-4 grid grid-cols-3 gap-3">
        <div className="min-w-0">
          <dt className="text-[12px] text-[var(--hr-muted)]">Arbeidstimer</dt>
          <dd className="hr-num mt-0.5 text-[17px] font-semibold text-[var(--hr-heading)]">{hoursText(per.hours)}</dd>
        </div>
        <div className="min-w-0">
          <dt className="text-[12px] text-[var(--hr-muted)]">Maskintimer</dt>
          <dd className="hr-num mt-0.5 text-[17px] font-semibold text-[var(--hr-heading)]">{hoursText(per.machineHours)}</dd>
        </div>
        <div className="min-w-0">
          <dt className="text-[12px] text-[var(--hr-muted)]">Vareforbruk</dt>
          <dd className={cn('hr-tabular mt-0.5 text-[14px] font-medium leading-5', forbruk ? 'text-[var(--hr-heading)]' : 'text-[var(--hr-muted-2)]')}>{forbruk ?? 'Ingen'}</dd>
        </div>
      </dl>
      <div className="mt-4 flex flex-wrap items-center gap-x-3 gap-y-1 text-[13px] text-[var(--hr-muted)]">
        <span className="hr-tabular">{per.timeCount} {per.timeCount === 1 ? 'føring' : 'føringer'}</span>
        {per.pending > 0 && <span className="hr-tabular text-[var(--hr-amber)]">{per.pending} til godkjenning</span>}
        {p.subprojects.length > 0 && <span>{p.subprojects.length} underprosjekter</span>}
        {p.lastActivity && <span className="hr-tabular">Sist {formatDateShort(p.lastActivity)}</span>}
        <span className="ml-auto inline-flex items-center gap-1 font-medium text-[var(--hr-accent-text)] opacity-0 transition-opacity duration-150 group-hover/card:opacity-100 group-focus-visible/card:opacity-100">
          Åpne <ArrowUpRight className="h-3 w-3" aria-hidden="true" />
        </span>
      </div>
    </Link>
  );
}

export default function ProsjekterPage() {
  const { refs } = useDriftRefs();
  const f = useDriftFilters({ keys: NO_KEYS });
  const view = f.params.get('prosjektstatus') ?? 'aktiv';
  const [data, setData] = useState(null);
  const [status, setStatus] = useState('loading');

  useEffect(() => {
    document.title = 'Prosjekter · Hardanger Rock';
  }, []);

  const queryKey = JSON.stringify({ ...f.query, status: view === 'alle' ? undefined : view });
  const load = useCallback(async () => {
    setStatus((s) => (s === 'ready' ? 'refreshing' : 'loading'));
    try {
      setData(await listProjects(JSON.parse(queryKey)));
      setStatus('ready');
    } catch {
      setStatus('error');
    }
  }, [queryKey]);

  useEffect(() => {
    load();
  }, [load]);

  const projects = data?.projects ?? [];
  const totalHours = projects.reduce((a, p) => a + p.period.hours, 0);
  const totalMachine = projects.reduce((a, p) => a + p.period.machineHours, 0);
  const pending = projects.reduce((a, p) => a + p.period.pending, 0);

  return (
    <div className="space-y-5">
      <PageHeader title="Prosjekter" lead="Timer, maskintimer, tillegg og vareforbruk samlet per prosjekt. Tallene gjelder valgt periode." />

      <ExampleDataNote>Kunder og prosjekter er fiktive. Ingen inntekter, satser eller fakturagrunnlag – kun tid og mengder.</ExampleDataNote>

      <Panel className="min-w-0">
        <PanelHeader
          title="Prosjektliste"
          description={data ? `${projects.length} ${projects.length === 1 ? 'prosjekt' : 'prosjekter'} · ${hoursText(totalHours)} arbeid · ${hoursText(totalMachine)} maskin${pending ? ` · ${fmtNumber(pending, 0)} føringer til godkjenning` : ''}` : 'Velg periode. Åpne et prosjekt for detaljer og registrering.'}
        />
        <DriftFilterBar f={f} refs={refs} showProject={false} testPrefix="prosjekter">
          <div className="inline-flex h-9 w-full rounded-[10px] border border-[var(--hr-border)] bg-white p-0.5 sm:w-auto" role="tablist" aria-label="Prosjektstatus" data-testid="prosjekter-view">
            {VIEWS.map((v) => (
              <button key={v.id} type="button" role="tab" aria-selected={view === v.id} onClick={() => f.set('prosjektstatus', v.id === 'aktiv' ? 'alle' : v.id)} className={cn('h-full flex-1 rounded-[8px] px-3 text-[13px] font-medium transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--hr-focus)] sm:flex-none', view === v.id ? 'bg-[var(--hr-heading)] text-white' : 'text-[var(--hr-muted)] hover:text-[var(--hr-heading)]')} data-testid={`prosjekter-view-${v.id}`}>
                {v.label}
              </button>
            ))}
          </div>
        </DriftFilterBar>

        {status === 'loading' && (
          <div className="grid gap-px bg-[var(--hr-hairline)] md:grid-cols-2 xl:grid-cols-3" aria-busy="true" aria-label="Laster prosjekter" data-testid="prosjekter-loading">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="space-y-3 bg-white px-5 py-4">
                <Skeleton className="h-4 w-40" />
                <Skeleton className="h-3 w-56" />
                <div className="grid grid-cols-3 gap-3 pt-2">
                  <Skeleton className="h-8" />
                  <Skeleton className="h-8" />
                  <Skeleton className="h-8" />
                </div>
              </div>
            ))}
          </div>
        )}
        {status === 'error' && (
          <div className="p-5">
            <Note tone="amber" data-testid="prosjekter-load-error">
              Kunne ikke hente prosjektene. <Button variant="link" className="h-auto px-1" onClick={load} data-testid="prosjekter-load-retry">Prøv igjen</Button>
            </Note>
          </div>
        )}
        {(status === 'ready' || status === 'refreshing') && projects.length === 0 && (
          <div className="p-5">
            <EmptyState title="Ingen prosjekter i utvalget" description="Velg en annen status eller periode." onReset={f.reset} />
          </div>
        )}
        {(status === 'ready' || status === 'refreshing') && projects.length > 0 && (
          <div className="grid gap-px bg-[var(--hr-hairline)] md:grid-cols-2 xl:grid-cols-3" data-testid="prosjekter-grid">
            {projects.map((p) => (
              <ProjectCard key={p.id} p={p} refs={refs} search={f.carry({ prosjektstatus: view === 'aktiv' ? undefined : view })} />
            ))}
          </div>
        )}
      </Panel>

      <Panel>
        <div className="flex flex-col gap-3 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-3">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] bg-[var(--hr-inset)] text-[var(--hr-muted)]">
              <FolderKanban className="h-4 w-4" aria-hidden="true" />
            </div>
            <div>
              <div className="text-[14px] font-semibold text-[var(--hr-heading)]">Rapporter per prosjekt</div>
              <p className="text-[13px] leading-5 text-[var(--hr-muted)]">Prosjektforbruk viser timer, maskintimer, tillegg og vareforbruk per vare og enhet for alle prosjekter i perioden.</p>
            </div>
          </div>
          <Button asChild variant="outline" className="h-9 shrink-0 rounded-[10px] bg-white text-[14px]">
            <Link to={`/rapporter/prosjektforbruk${f.carry({})}`} data-testid="prosjekter-report-link">Åpne prosjektforbruk</Link>
          </Button>
        </div>
      </Panel>
    </div>
  );
}
